import { useLocation, useParams } from "react-router-dom"
import { Location, MarkerLocation, Walk } from "../../../../Interfaces"
import Map from "../../../../components/maps/Map"
import { useEffect, useRef, useState, useCallback } from "react"
import { locationService } from "../../../../services/locationService"
import { FaRedo, FaSave, FaUndo } from "react-icons/fa"
import ConfirmationModal from "../../../../components/modals/ConfirmationModal"
import NewWalkModal from "../../../../components/modals/NewWalkModal"
import { walkService } from "../../../../services/walkService"
import PopupModal from "../../../../components/modals/PopupModal"
import LocationHeader from "../../../../components/locations/LocationHeader"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

let trashedMarkers: MarkerLocation[] = []

function SortableItem({ marker, onEditRange }: { marker: MarkerLocation; onEditRange: (marker: MarkerLocation) => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: marker.label.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-secondary p-2 rounded text-white hover:bg-secondary-light group"
    >
      <div
        className={`flex-1 ${marker.unitNumberRange ? 'cursor-pointer' : ''} pr-2`}
        onClick={marker.unitNumberRange ? () => onEditRange(marker) : undefined}
      >
        <span>{marker.unitNumberRange || `Point ${marker.label}`}</span>
      </div>
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing"
      >
        <span className="bg-primary px-2 py-1 rounded text-sm">{marker.label}</span>
      </div>
    </div>
  );
}

export default function CreatWalks() {
  const location = useLocation()
  const { id } = useParams()
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null)
  const [zoom, setZoom] = useState<number>(18)
  const [savingWalkStatus, setSavingWalkStatus] = useState<null | string>(null)
  const [markers, setMarkers] = useState<MarkerLocation[]>([])
  const [walks, setWalks] = useState<Walk[]>([])
  const [currentWalk, setCurrentWalk] = useState<null | Walk>(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false)
  const [showNewWalkModal, setShowNewWalkModal] = useState<boolean>(false)
  const [walkName, setWalkName] = useState<string>("")
  const [walkDescription, setWalkDescription] = useState<string>("")
  const [showNumberRangeModal, setShowNumberRangeModal] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [numberRangeInput, setNumberRangeInput] = useState("")
  const [selectedMarker, setSelectedMarker] = useState<MarkerLocation | null>(null)
  const coordinates = useRef<google.maps.LatLngLiteral>({ lat: 0, lng: 0 })
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0)
  const [isProcessingMarker, setIsProcessingMarker] = useState(false)
  const [organizationName, setOrganizationName] = useState<string>("")
  const [managementCompanyName, setManagementCompanyName] = useState<string>("")

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchWalks = useCallback(async () => {
    if (!currentLocation?.id) return;
    try {
      const fetchedWalks = await walkService.getAll(currentLocation.id)
      console.log("Raw API response from /api/walk/all:", JSON.stringify(fetchedWalks, null, 2))
      fetchedWalks?.forEach((walk: Walk, index: number) => {
        console.log(`Walk ${index + 1} (${walk.name}):`, JSON.stringify(walk, null, 2))
        console.log(`Walk ${index + 1} markers:`, JSON.stringify(walk.markers, null, 2))
      })
      const filteredWalks = fetchedWalks?.filter((walk: Walk) => walk.deletedAt === null)
      setWalks(filteredWalks || [])
    } catch (error) {
      console.error("Error fetching walks:", error)
      setWalks([])
    }
  }, [currentLocation?.id])

  useEffect(() => {
    const fetchLocation = async () => {
      if (!id) return
      try {
        const locationData = await locationService.getOne(Number(id))
        if (locationData) {
          setCurrentLocation(locationData)
          coordinates.current = {
            lat: locationData.latitude,
            lng: locationData.longitude
          }
          if (locationData.zoomLevel) {
            setZoom(locationData.zoomLevel)
          }
        }
      } catch (error) {
        console.error("Error fetching location:", error)
      }
    }

    // If we have location state, use it; otherwise fetch the location
    if (location.state?.currentLocation) {
      const locationData = location.state.currentLocation
      setCurrentLocation(locationData)
      coordinates.current = {
        lat: locationData.latitude,
        lng: locationData.longitude
      }
      if (locationData.zoomLevel) {
        setZoom(locationData.zoomLevel)
      }
    } else {
      fetchLocation()
    }
  }, [id, location.state])

  useEffect(() => {
    if (currentLocation?.id) {
      fetchWalks()
    }
  }, [currentLocation, fetchWalks])

  useEffect(() => {
    const fetchOrganizationName = async () => {
      if (!id) return
      try {
        const name = await locationService.getOrganizationName(Number(id))
        setOrganizationName(name)
      } catch (error) {
        console.error("Error fetching organization name:", error)
      }
    }

    const fetchManagementCompanyName = async () => {
      if (!id) return
      try {
        const name = await locationService.getManagementCompanyName(Number(id))
        setManagementCompanyName(name)
      } catch (error) {
        console.error("Error fetching management company name:", error)
      }
    }

    fetchOrganizationName()
    fetchManagementCompanyName()
  }, [id])

  useEffect(() => {
    const currentMarker = markers[currentMarkerIndex]
    if (!currentMarker || isProcessingMarker || showNumberRangeModal) return

    // Don't reopen modal if the marker already has a unit range or is completed
    if ((!currentMarker.lat && !currentMarker.lng) && !currentMarker.isCompleted && !currentMarker.unitNumberRange) {
      setIsProcessingMarker(true)
      setShowNumberRangeModal(true)
    }
  }, [currentMarkerIndex, markers, isProcessingMarker, showNumberRangeModal])

  useEffect(() => {
    console.log("Modal state changed:", { showNumberRangeModal, isProcessingMarker })
  }, [showNumberRangeModal, isProcessingMarker])

  const handleSaveWalk = async () => {
    trashedMarkers = []
    console.log("Saving walk with currentWalk:", currentWalk)

    // For new walks, create new walk
    if (!currentWalk) {
      if (!currentLocation?.id || !walkName) return
      const markersToSave = markers.map(marker => ({
        ...marker,
        hasUnitRange: !!marker.unitNumberRange
      }))
      console.log("Creating new walk with markers:", markersToSave)
      const walkInfo = {
        name: walkName,
        description: walkDescription,
        locationId: currentLocation.id,
        markers: markersToSave
      }
      setSavingWalkStatus("Saving...")
      try {
        const status = await walkService.create(walkInfo)
        if (status) {
          setSavingWalkStatus("Saved!")
          setTimeout(async () => {
            await fetchWalks()
            setSavingWalkStatus(null)
            setShowNewWalkModal(false)
            setMarkers([])
            setCurrentWalk(null)  // Ensure currentWalk is cleared
            setWalkName("")       // Clear walk name
            setWalkDescription("") // Clear description
          }, 2000)
        }
      } catch (error) {
        console.error("Error creating new walk:", error)
        setSavingWalkStatus("Error")
        setTimeout(() => {
          setSavingWalkStatus(null)
        }, 1500)
      }
      return
    }

    // If we get here, we're editing an existing walk
    console.log("Updating walk:", {
      walkId: currentWalk.id,
      walkName: currentWalk.name,
      currentMarkers: markers.length
    })

    // When editing a walk, we create a new version and mark the old one as deleted
    // This preserves walk history and allows for rollback if needed
    const newMarkers = markers.map((marker) => {
      const { id, ...markerWithoutId } = marker
      return {
        ...markerWithoutId,
        walkId: currentWalk.id,  // This will be updated to the new walk's ID after creation
        hasUnitRange: !!marker.unitNumberRange
      }
    })

    if (!currentLocation?.id) return
    const walkInfo = {
      name: currentWalk.name,
      description: currentWalk.description,
      locationId: currentLocation.id,
      markers: newMarkers
    }
    setSavingWalkStatus("Saving...")

    try {
      console.log("Creating new version of walk...")
      const newWalk = await walkService.create(walkInfo)

      if (newWalk) {
        console.log("New walk created, marking old walk as deleted:", currentWalk.id)
        await walkService.delete(currentWalk.id)

        setSavingWalkStatus("Saved!")
        setTimeout(async () => {
          await fetchWalks()
          setSavingWalkStatus(null)
          setShowConfirmationModal(false)
          setMarkers([])
          setCurrentWalk(null)
        }, 2000)
      }
    } catch (error) {
      console.error("Error updating walk:", error)
      setSavingWalkStatus("Error")
      setTimeout(() => {
        setSavingWalkStatus(null)
      }, 1500)
    }
  }

  const handleUndo = () => {
    if (!markers.length) return
    const trashedMarker = markers.pop()
    if (!trashedMarker) return
    trashedMarkers.push(trashedMarker)
    setMarkers((current) => [...current])
  }

  const handleRedo = () => {
    if (!trashedMarkers.length) return
    const restoredMarker = trashedMarkers.pop()
    markers.push(restoredMarker!!)
    setMarkers((current) => [...current])
  }

  const handleLoadWalk = (walkId: string) => {
    if (walkId === "") {
      setCurrentWalk(null)
      setMarkers([])
      return
    }

    const selectedWalk = walks.find(walk => walk.id.toString() === walkId)
    if (selectedWalk) {
      setCurrentWalk(selectedWalk)
      setMarkers(selectedWalk.markers || [])
    }
  }

  const handleMarkers = (newMarkers: MarkerLocation[]) => {
    trashedMarkers = []
    console.log("Adding new markers:", newMarkers)

    // If we're getting a single marker and it's a new one (no id), add it to the list
    if (newMarkers.length === 1 && !newMarkers[0].id) {
      setMarkers(prev => [...prev, newMarkers[0]])
      setCurrentMarkerIndex(prev => prev + 1)
      return
    }

    // If we're getting multiple markers or an update, replace the entire list
    setMarkers(newMarkers)
  }

  const handleDeleteWalk = async () => {
    if (!currentWalk) return
    try {
      const status = await walkService.delete(currentWalk.id)
      if (status) {
        const updatedWalks = walks.filter((walk) => walk.id !== currentWalk.id)
        setWalks(updatedWalks)
        setShowDeleteConfirmationModal(false)
        setCurrentWalk(null)
        setMarkers([])
      }
    } catch (error) {
      console.error("Error deleting walk:", error)
      // You could show an error message to the user here if needed
    }
  }

  const handleWalkName = (e: { target: { value: string } }) => {
    setWalkName(e.target.value)
  }

  const handleWalkDescription = (e: { target: { value: string } }) => {
    setWalkDescription(e.target.value)
  }

  const handleLocationAdjustment = (latLng: google.maps.LatLng | google.maps.LatLngLiteral) => {
    coordinates.current.lat = Number(latLng.lat)
    coordinates.current.lng = Number(latLng.lng)
  }

  const handleEditRange = (marker: MarkerLocation) => {
    console.log("Opening modal for editing marker:", marker)
    setSelectedMarker(marker);
    setNumberRangeInput(marker.unitNumberRange || "");
    setShowNumberRangeModal(true);
  };

  const handleNumberRangeInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Only allow numbers, commas, dashes, and spaces
    const sanitizedValue = e.target.value.replace(/[^0-9,\s-]/g, '');
    setNumberRangeInput(sanitizedValue);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMarkers((items) => {
        const oldIndex = items.findIndex(item => item.label.toString() === active.id);
        const newIndex = items.findIndex(item => item.label.toString() === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({
          ...item,
          label: index + 1
        }));
      });
    }
  };

  if (!currentLocation) return null

  return (
    <div className="w-full h-[calc(100vh-8rem)] flex justify-center overflow-hidden">
      <div className="w-full flex flex-col h-full">
        <LocationHeader
          location={currentLocation}
          activeTab="Setup Walks"
          organizationName={organizationName}
          managementCompanyName={managementCompanyName}
        />

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-lg p-4 flex-1 flex flex-col min-h-0">
          <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
            {/* Map Section */}
            <div className="w-full md:w-2/3 flex-1 min-h-0">
              <div className="h-full min-h-0">
                <Map
                  markers={markers}
                  setMarkers={handleMarkers}
                  handleLocationAdjustment={handleLocationAdjustment}
                  coordinates={coordinates.current}
                  zoom={zoom}
                  setZoom={setZoom}
                  name={currentLocation.name}
                  onDoubleClick={(latLng) => {
                    const newMarker: MarkerLocation = {
                      label: markers.length + 1,
                      lat: latLng.lat,
                      lng: latLng.lng,
                      hasUnitRange: false,
                      isCompleted: false
                    }
                    handleMarkers([newMarker])
                  }}
                />
              </div>
            </div>

            {/* Controls Section */}
            <div className="w-full md:w-1/3 overflow-y-auto">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Walk Setup</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCurrentWalk(null);
                        setMarkers([]);
                        setWalkName("");
                        setWalkDescription("");
                        setShowInfoModal(true);
                      }}
                      className="p-2 bg-green-500 text-white rounded hover:bg-green-600"
                      title="Create New Walk"
                    >
                      Create New Walk
                    </button>
                    <button
                      onClick={handleUndo}
                      className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                      title="Undo"
                    >
                      <FaUndo />
                    </button>
                    <button
                      onClick={handleRedo}
                      className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                      title="Redo"
                    >
                      <FaRedo />
                    </button>
                    <button
                      onClick={() => {
                        if (!currentWalk) {
                          // For new walks, show the new walk modal first
                          setShowNewWalkModal(true);
                        } else {
                          // For existing walks, show confirmation modal
                          setShowConfirmationModal(true);
                        }
                      }}
                      className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      title="Save Walk"
                      disabled={markers.length === 0}
                    >
                      <FaSave />
                    </button>
                  </div>
                </div>

                {/* Walk Selection */}
                <div className="mb-4">
                  <select
                    value={currentWalk?.id || ""}
                    onChange={(e) => handleLoadWalk(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select a walk...</option>
                    {walks.map((walk) => (
                      <option key={walk.id} value={walk.id}>
                        {walk.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Markers List */}
                <div className="space-y-2">
                  <h3 className="font-medium mb-2">Markers</h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={markers.map(m => m.label.toString())}
                      strategy={verticalListSortingStrategy}
                    >
                      {markers.map((marker) => (
                        <SortableItem
                          key={marker.label}
                          marker={marker}
                          onEditRange={handleEditRange}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Add Unit Number Range Button */}
                {(showNewWalkModal || currentWalk) && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowNumberRangeModal(true)}
                      className="w-full p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      disabled={!markers.length}
                      title="Add a marker for a range of unit numbers (e.g., '101-105' or '201, 203, 205')"
                    >
                      Add Unit Number Range
                    </button>
                    <p className="text-sm text-gray-500 mt-1">
                      Use this to add markers for unit numbers without physical locations
                    </p>
                  </div>
                )}

                {/* Delete Walk Button */}
                {currentWalk && (
                  <div className="mt-6 border-t pt-4">
                    <button
                      onClick={() => setShowDeleteConfirmationModal(true)}
                      className="w-full p-2 bg-red-500 text-white rounded hover:bg-red-600"
                      title="Delete this walk"
                    >
                      Delete Walk
                    </button>
                    <p className="text-sm text-gray-500 mt-1">
                      This will permanently delete "{currentWalk.name}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showInfoModal && (
        <PopupModal
          isOpen={showInfoModal}
          onCancel={() => setShowInfoModal(false)}
        >
          <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Create New Walk</h2>
            <p className="mb-4">Double click on the locations the manager will need to walk through to complete the walk.</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowInfoModal(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                OK
              </button>
            </div>
          </div>
        </PopupModal>
      )}

      {showConfirmationModal && (
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onConfirm={handleSaveWalk}
          onCancel={() => setShowConfirmationModal(false)}
          title="Save Walk"
          message="Are you sure you want to save this walk?"
        />
      )}

      {showDeleteConfirmationModal && (
        <ConfirmationModal
          isOpen={showDeleteConfirmationModal}
          onConfirm={handleDeleteWalk}
          onCancel={() => setShowDeleteConfirmationModal(false)}
          title="Delete Walk"
          message="Are you sure you want to delete this walk?"
        />
      )}

      {showNewWalkModal && (
        <NewWalkModal
          isOpen={showNewWalkModal}
          title={`${currentLocation.name}`}
          message={"Give this walk a name and brief description."}
          onConfirm={() => {
            setShowNewWalkModal(false)
            handleSaveWalk()
          }}
          onCancel={() => setShowNewWalkModal(false)}
          handleWalkName={handleWalkName}
          handleWalkDescription={handleWalkDescription}
          walkName={walkName}
          walkDescription={walkDescription}
          status={savingWalkStatus}
        />
      )}

      {showNumberRangeModal && (
        <PopupModal
          isOpen={showNumberRangeModal}
          onCancel={() => {
            setShowNumberRangeModal(false)
            setIsProcessingMarker(false)
          }}
        >
          <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Enter Unit Number Range</h2>
            <textarea
              value={numberRangeInput}
              onChange={handleNumberRangeInputChange}
              className="w-full p-2 border rounded"
              placeholder="Enter unit number range..."
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowNumberRangeModal(false)
                  setIsProcessingMarker(false)
                }}
                className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedMarker) {
                    selectedMarker.unitNumberRange = numberRangeInput
                    setNumberRangeInput("")
                    setShowNumberRangeModal(false)
                    setIsProcessingMarker(false)
                  } else {
                    // Create a new marker with the unit number range
                    const newMarker: MarkerLocation = {
                      label: markers.length + 1,
                      unitNumberRange: numberRangeInput,
                      hasUnitRange: true,
                      isCompleted: true
                    }
                    handleMarkers([newMarker])
                    setNumberRangeInput("")
                    setShowNumberRangeModal(false)
                    setIsProcessingMarker(false)
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </PopupModal>
      )}
    </div>
  )
}
